/**
 * Aplica no localStorage o que veio do Dynamo via GET /api/admin/catalog.
 * Notícias / estabelecimentos vêm das tabelas Dynamo quando configuradas na API.
 */

import { EXCURSOES_STORAGE_KEY } from '../../../shared/lib/excursaoStorage';
import { KEY_ASSOCIACOES_STORAGE } from './associacoesStorage';
import type { AdminSyncPayload } from './adminSyncApi';
import { CLIENTE_HOME_LAYOUT_STORAGE_KEY } from '../../../shared/lib/homeExperiencesLayoutConfig';
import { decodeLiteralUnicodeEscapesDeep } from '../../../shared/lib/decodeLiteralUnicodeEscapes';

const KEY_CIDADES = 'naviex-admin-cidades';
const KEY_CATEGORIAS = 'naviex-admin-categorias';
const KEY_SUBCATEGORIAS = 'naviex-admin-subcategorias';
const KEY_EXPERIENCIAS = 'naviex-admin-experiencias';
const KEY_TARIFARIOS = 'naviex-admin-tarifarios';
const KEY_TARIFARIO_GLOBAL = 'naviex-admin-tarifario-global';
const KEY_GUIAS = 'naviex-admin-guias';
const KEY_AGENCIAS = 'naviex-admin-agencias';
const KEY_CLIENTES = 'naviex-clientes-registro';
const KEY_SOLICITACOES = 'naviex-solicitacoes-fornecedor';
const KEY_NEWS_ARTICLES = 'naviex-admin-news-articles';
const KEY_NEWS_CATEGORIES = 'naviex-admin-news-categories';
const KEY_NEWS_SUBCATEGORIES = 'naviex-admin-news-subcategories';
const KEY_NEWS_LIMITS_BY_CITY = 'naviex-admin-news-limits-by-city';
const KEY_ESTABELECIMENTOS = 'naviex-admin-estabelecimentos';
const KEY_ESTABELECIMENTO_CATEGORIAS = 'naviex-admin-estabelecimentos-categorias';
const KEY_ESTABELECIMENTO_SUBCATEGORIAS = 'naviex-admin-estabelecimentos-subcategorias';
const KEY_HOSPEDAGENS = 'naviex-admin-hospedagens';
const KEY_EVENTS = 'naviex-admin-events';
const KEY_EVENT_CATEGORIES = 'naviex-admin-event-categories';
const KEY_EVENT_TAGS = 'naviex-admin-event-tags';

function setJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('[adminCatalogHydration] Falha ao gravar', key, e);
  }
}

/** Escreve somente chaves espelhadas no POST /api/admin/sync (tabelas Dynamo). */
export function applyDynamoCatalogToLocalStorage(payload: AdminSyncPayload): void {
  if (!payload || typeof payload !== 'object') return;

  setJson(KEY_CIDADES, Array.isArray(payload.cidades) ? payload.cidades : []);
  setJson(KEY_CATEGORIAS, Array.isArray(payload.categorias) ? payload.categorias : []);
  setJson(KEY_SUBCATEGORIAS, Array.isArray(payload.subcategorias) ? payload.subcategorias : []);
  setJson(KEY_EXPERIENCIAS, Array.isArray(payload.experiencias) ? payload.experiencias : []);
  setJson(KEY_TARIFARIOS, Array.isArray(payload.tarifarios) ? payload.tarifarios : []);

  const tg = payload.tarifarioGlobal;
  if (tg && typeof tg === 'object' && !Array.isArray(tg)) {
    setJson(KEY_TARIFARIO_GLOBAL, tg);
  } else {
    setJson(KEY_TARIFARIO_GLOBAL, {});
  }

  setJson(KEY_GUIAS, Array.isArray(payload.guias) ? payload.guias : []);
  setJson(KEY_AGENCIAS, Array.isArray(payload.agencias) ? payload.agencias : []);
  setJson(
    KEY_ASSOCIACOES_STORAGE,
    Array.isArray(payload.associacoes) ? payload.associacoes : []
  );
  setJson(KEY_CLIENTES, Array.isArray(payload.clientes) ? payload.clientes : []);
  setJson(KEY_SOLICITACOES, Array.isArray(payload.solicitacoesFornecedor) ? payload.solicitacoesFornecedor : []);

  setJson(
    KEY_NEWS_ARTICLES,
    Array.isArray(payload.newsArticles)
      ? payload.newsArticles.map((a) => decodeLiteralUnicodeEscapesDeep(a))
      : []
  );
  setJson(
    KEY_NEWS_CATEGORIES,
    Array.isArray(payload.newsCategories)
      ? payload.newsCategories.map((c) => decodeLiteralUnicodeEscapesDeep(c))
      : []
  );
  setJson(
    KEY_NEWS_SUBCATEGORIES,
    Array.isArray(payload.newsSubcategories)
      ? payload.newsSubcategories.map((s) => decodeLiteralUnicodeEscapesDeep(s))
      : []
  );
  const limits = payload.newsLimitsByCity;
  if (limits && typeof limits === 'object' && !Array.isArray(limits)) {
    setJson(KEY_NEWS_LIMITS_BY_CITY, limits);
  } else {
    setJson(KEY_NEWS_LIMITS_BY_CITY, {});
  }
  setJson(KEY_ESTABELECIMENTOS, Array.isArray(payload.estabelecimentos) ? payload.estabelecimentos : []);
  setJson(
    KEY_ESTABELECIMENTO_CATEGORIAS,
    Array.isArray(payload.estabelecimentoCategorias) ? payload.estabelecimentoCategorias : []
  );
  setJson(
    KEY_ESTABELECIMENTO_SUBCATEGORIAS,
    Array.isArray(payload.estabelecimentoSubcategorias) ? payload.estabelecimentoSubcategorias : []
  );

  setJson(KEY_HOSPEDAGENS, Array.isArray(payload.hospedagens) ? payload.hospedagens : []);
  setJson(KEY_EVENTS, Array.isArray(payload.events) ? payload.events : []);
  setJson(KEY_EVENT_CATEGORIES, Array.isArray(payload.eventCategories) ? payload.eventCategories : []);
  setJson(KEY_EVENT_TAGS, Array.isArray(payload.eventTags) ? payload.eventTags : []);

  const exRows = Array.isArray(payload.excursoes) ? payload.excursoes : [];
  const exMap: Record<string, unknown> = {};
  for (const row of exRows) {
    if (row && typeof row === 'object' && 'id' in row) {
      const id = String((row as { id: unknown }).id).trim();
      if (id) exMap[id] = row;
    }
  }
  setJson(EXCURSOES_STORAGE_KEY, exMap);

  const chl = (payload as { clienteHomeLayout?: unknown }).clienteHomeLayout;
  if (chl != null && typeof chl === 'object') {
    try {
      localStorage.setItem(CLIENTE_HOME_LAYOUT_STORAGE_KEY, JSON.stringify(chl));
    } catch (e) {
      console.error('[adminCatalogHydration] clienteHomeLayout', e);
    }
  }
}
